/* Docx */
.documentContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  min-height: 100vh;
}

.documentContent {
  padding: 40px;
  max-width: 800px;
  border-radius: 8px;
  font-family: "Times New Roman", Times, serif;
  line-height: 1.4;
}

.documentContent h1,
.documentContent h2,
.documentContent h3,
.documentContent h4,
.documentContent h5,
.documentContent h6 {
  margin-top: 20px;
  margin-bottom: 10px;
  font-weight: bold;
  line-height: 1.2;
}

.documentContent p {
  margin-bottom: 16px;
}

.documentContent img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 20px 0;
}

.documentContent a {
  color: hsl(var(--primary-ink));
  text-decoration: underline;
  text-underline-offset: 2px;
}

.documentContent a:hover {
  text-decoration-thickness: 2px;
}

/* Citation highlight in document preview (global class from dom-highlight) */
.documentContent :global(.citation-highlight) {
  background-color: rgba(147, 51, 234, 0.25);
  border-radius: 2px;
}

.documentContent :global(.citation-highlight-fuzzy) {
  background-color: rgba(234, 179, 8, 0.25);
  border-bottom: 1px dashed rgba(234, 179, 8, 0.6);
}

.documentContent :global(.citation-highlight-partial) {
  background-color: rgba(59, 130, 246, 0.25);
  border: 2px dotted rgba(59, 130, 246, 0.6);
}

.documentContent :global(.citation-highlight-weak) {
  background: repeating-linear-gradient(
    45deg,
    rgba(239, 68, 68, 0.15),
    rgba(239, 68, 68, 0.15) 10px,
    rgba(239, 68, 68, 0.25) 10px,
    rgba(239, 68, 68, 0.25) 20px
  );
  border-bottom: 2px solid rgba(239, 68, 68, 0.8);
}

/* Shared highlight scope for non-viewer citation previews (e.g. hover peek cards). */
.citationHighlightScope :global(.citation-highlight) {
  background-color: rgba(147, 51, 234, 0.25);
  border-radius: 2px;
}

.citationHighlightScope :global(.citation-highlight-fuzzy) {
  background-color: rgba(234, 179, 8, 0.25);
  border-bottom: 1px dashed rgba(234, 179, 8, 0.6);
}

.citationHighlightScope :global(.citation-highlight-partial) {
  background-color: rgba(59, 130, 246, 0.25);
  border: 2px dotted rgba(59, 130, 246, 0.6);
}

.citationHighlightScope :global(.citation-highlight-weak) {
  background: repeating-linear-gradient(
    45deg,
    rgba(239, 68, 68, 0.15),
    rgba(239, 68, 68, 0.15) 10px,
    rgba(239, 68, 68, 0.25) 10px,
    rgba(239, 68, 68, 0.25) 20px
  );
  border-bottom: 2px solid rgba(239, 68, 68, 0.8);
}

/* Excel */
.spreadSheetReadOnly {
  color: black !important;
  background-color: white !important;
  opacity: 1 !important;
  cursor: default;
}

.spreadSheetReadOnlyCell {
  color: black !important;
  background-color: white !important;
  opacity: 1 !important;
  cursor: default;
}

/* Excel viewer layout - header and tabs fixed, only spreadsheet scrolls */
.excelViewerContainer {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.excelViewerContainer > .infoInnerContainer,
.excelViewerContainer > .excelTabs {
  flex-shrink: 0;
}

.excelSpreadsheetWrapper {
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
}

/* Keep spreadsheet content wider/taller than viewport when needed,
   so both horizontal and vertical cell scrollbars are available. */
.excelSpreadsheetGrid {
  min-width: max-content;
}

.excelSpreadsheetGrid :global(table) {
  min-width: 100%;
  width: max-content;
}

/* Excel sheet tabs - single line with horizontal scroll, Excel-like */
.excelTabs {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-gutter: stable;
}

.excelTab,
.excelTabActive {
  flex-shrink: 0;
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 4px;
  border: 1px solid hsl(var(--border));
  background-color: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
}

.excelTab:hover {
  background-color: hsl(var(--muted) / 0.8);
}

.excelTabActive {
  background-color: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border-color: hsl(var(--primary));
}

/* Citation highlight in Excel cells */
.spreadSheetReadOnlyCell.citationHighlight {
  background-color: rgba(147, 51, 234, 0.25) !important;
  border-radius: 2px;
}

/* FallbackViewer */
.fallbackViewer {
  text-align: center;
  padding: 1.875rem;
  background-color: var(--custom-primary-color);
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 0.625rem rgba(0, 0, 0, 0.1);
  max-width: 25rem;
  margin: 1.25rem auto;
  font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 12rem;
}

.fallbackViewer h2 {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1.125rem;
  color: hsl(var(--foreground));
  margin-bottom: 1.25rem;
}

.infoContainer {
  margin-top: 1rem;
  overflow-x: auto;
}

.infoInnerContainer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--custom-primary-color);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  width: 100%;
  text-align: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.infoInnerContainer h2 {
  font-size: 1.2rem;
}

.pdfContainer {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pdfContainer :global(.react-pdf__Document) {
  display: flex;
  justify-content: center;
}

.pdfContainer :global(.react-pdf__Page) {
  margin: 0 auto;
}

.pdfPagination {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background-color: hsl(var(--background) / 0.95);
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(8px);
  z-index: 10;
}

.pageInfo {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(var(--foreground));
  user-select: none;
  min-width: 80px;
  text-align: center;
}

.pdfContainer :global(.citation-highlight) {
  /* react-pdf text layer spans use color:transparent and absolute positioning.
     We need to make the background visible despite the transparent text. */
  background-color: rgba(147, 51, 234, 0.3) !important;
  border-radius: 2px;
  color: transparent !important;
}

.pdfContainer :global(.citation-highlight-fuzzy) {
  background-color: rgba(234, 179, 8, 0.35) !important;
  border-bottom: 2px dashed rgba(234, 179, 8, 0.8);
}

.pdfContainer :global(.citation-highlight-partial) {
  background-color: rgba(59, 130, 246, 0.25) !important;
  border: 2px dotted rgba(59, 130, 246, 0.6);
  color: transparent !important;
}

.pdfContainer :global(.citation-highlight-weak) {
  background: repeating-linear-gradient(
    45deg,
    rgba(239, 68, 68, 0.15),
    rgba(239, 68, 68, 0.15) 10px,
    rgba(239, 68, 68, 0.25) 10px,
    rgba(239, 68, 68, 0.25) 20px
  ) !important;
  border-bottom: 2px solid rgba(239, 68, 68, 0.8);
  color: transparent !important;
}

.fileTitle {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 80%;
  overflow: hidden;
}

.fileTitle h2 {
  margin: 0;
  padding: 0 0.5rem;
  font-size: 1.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.fileType {
  font-size: 0.8rem;
  color: hsl(var(--muted-foreground));
  padding: 0 0.5rem;
  margin-top: 0.2rem;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: hsl(var(--muted-foreground));
}
